import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Button,
  Input,
  Label,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui";
import { FileText, DollarSign, Building, CheckCircle, FolderOpen } from "lucide-react";
import { toast } from "react-hot-toast";
import financeService from "@/services/financeService";
import api from "@/services/api";

const SUBSIDIARIES = [
  { value: "puffin_group", label: "Puffin Group Ltd" },
  { value: "buildwise", label: "Buildwise Construction Ltd" },
  { value: "laser_shield", label: "Laser Shield Ltd." },
  { value: "maz_logistics", label: "Maz Logistics Ltd." },
  { value: "puffin_energy", label: "Puffin Energy Ltd." },
  { value: "swingtide", label: "Swingtide Consulting Ltd" },
  { value: "puffin_agro", label: "Puffin Agro Allied" },
];

const CreatePayableModal = ({ open, onOpenChange, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingBudgets, setLoadingBudgets] = useState(false);
  const [projects, setProjects] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [formData, setFormData] = useState({
    vendor_name: "",
    total_amount: "",
    subsidiary: "",
    project_id: "",
    budget_id: "",
    description: "",
  });
  const [errors, setErrors] = useState({});

  // Fetch projects and budgets when modal opens
  useEffect(() => {
    if (open) {
      setFormData({
        vendor_name: "",
        total_amount: "",
        subsidiary: "",
        project_id: "",
        budget_id: "",
        description: "",
      });
      setErrors({});
      fetchProjects();
      fetchBudgets();
    }
  }, [open]);

  const fetchProjects = async () => {
    try {
      setLoadingProjects(true);
      const response = await api.get("/projects", {
        params: {
          page: 1,
          limit: 1000,
          status: "active",
        },
      });
      
      if (response.data.success) {
        setProjects(response.data.data.projects || []);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchBudgets = async () => {
    try {
      setLoadingBudgets(true);
      const response = await api.get("/finance/budgets", {
        params: {
          page: 1,
          limit: 1000,
        },
      });
      
      console.log("Budgets response:", response.data);
      
      if (response.data.success) {
        const budgetsList = response.data.data.budgets || response.data.data || [];
        console.log("Budgets list:", budgetsList);
        if (budgetsList.length > 0) {
          console.log("First budget sample:", budgetsList[0]);
        }
        setBudgets(budgetsList);
      }
    } catch (error) {
      console.error("Error fetching budgets:", error);
      toast.error("Failed to load budgets");
    } finally {
      setLoadingBudgets(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.vendor_name.trim()) {
      newErrors.vendor_name = "Vendor name is required";
    }

    if (!formData.total_amount || parseFloat(formData.total_amount) <= 0) {
      newErrors.total_amount = "Total amount must be greater than zero";
    }

    if (!formData.subsidiary) {
      newErrors.subsidiary = "Subsidiary is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const payableData = {
        vendor_name: formData.vendor_name.trim(),
        total_amount: parseFloat(formData.total_amount),
        subsidiary: formData.subsidiary,
        project_id: formData.project_id ? parseInt(formData.project_id) : null,
        budget_id: formData.budget_id ? parseInt(formData.budget_id) : null,
        description: formData.description.trim() || null,
      };

      await financeService.createPayable(payableData);
      onSuccess();
    } catch (error) {
      console.error("Error creating payable:", error);
      toast.error(error.response?.data?.message || "Failed to create payable");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="space-y-6">
          {/* Header */}
          <div className="border-b pb-4">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FileText className="h-6 w-6 text-red-600" />
              Create Payable
            </h2>
            <p className="text-gray-600 mt-1">
              Create a new accounts payable entry for vendor payments
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Vendor Name */}
            <div>
              <Label htmlFor="vendor_name" className="text-sm font-medium text-gray-700">
                Vendor Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="vendor_name"
                type="text"
                value={formData.vendor_name}
                onChange={(e) => handleChange("vendor_name", e.target.value)}
                className={`mt-1 ${errors.vendor_name ? "border-red-500" : ""}`}
                placeholder="Enter vendor name"
                maxLength={255}
              />
              {errors.vendor_name && (
                <p className="text-sm text-red-600 mt-1">{errors.vendor_name}</p>
              )}
            </div>

            {/* Total Amount */}
            <div>
              <Label htmlFor="total_amount" className="text-sm font-medium text-gray-700">
                Total Amount <span className="text-red-500">*</span>
              </Label>
              <div className="relative mt-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-4 w-4 text-gray-400" />
                </div>
                <Input
                  id="total_amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={formData.total_amount}
                  onChange={(e) => handleChange("total_amount", e.target.value)}
                  className={`pl-10 ${errors.total_amount ? "border-red-500" : ""}`}
                  placeholder="0.00"
                />
              </div>
              {errors.total_amount && (
                <p className="text-sm text-red-600 mt-1">{errors.total_amount}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                You can adjust this amount later as needed
              </p>
            </div>

            {/* Subsidiary */}
            <div>
              <Label htmlFor="subsidiary" className="text-sm font-medium text-gray-700">
                Subsidiary <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.subsidiary}
                onValueChange={(value) => handleChange("subsidiary", value)}
              >
                <SelectTrigger className={`mt-1 ${errors.subsidiary ? "border-red-500" : ""}`}>
                  <div className="flex items-center gap-2">
                    <Building className="h-4 w-4 text-gray-400" />
                    <SelectValue placeholder="Select subsidiary" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {SUBSIDIARIES.map((subsidiary) => (
                    <SelectItem key={subsidiary.value} value={subsidiary.value}>
                      {subsidiary.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.subsidiary && (
                <p className="text-sm text-red-600 mt-1">{errors.subsidiary}</p>
              )}
            </div>

            {/* Project (Optional) */}
            <div>
              <Label htmlFor="project_id" className="text-sm font-medium text-gray-700">
                Project (Optional)
              </Label>
              <Select
                value={formData.project_id}
                onValueChange={(value) => handleChange("project_id", value)}
              >
                <SelectTrigger className="mt-1">
                  <div className="flex items-center gap-2">
                    <FolderOpen className="h-4 w-4 text-gray-400" />
                    <SelectValue placeholder={loadingProjects ? "Loading projects..." : "Select project"} />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">No project</SelectItem>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id.toString()}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Link to a project to deduct payments from project budget
              </p>
            </div>

            {/* Budget (Optional) */}
            <div>
              <Label htmlFor="budget_id" className="text-sm font-medium text-gray-700">
                Budget (Optional)
              </Label>
              <Select
                value={formData.budget_id}
                onValueChange={(value) => handleChange("budget_id", value)}
              >
                <SelectTrigger className="mt-1">
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-gray-400" />
                    <SelectValue placeholder={loadingBudgets ? "Loading budgets..." : "Select budget"} />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">No budget</SelectItem>
                  {budgets.length === 0 && !loadingBudgets ? (
                    <div className="px-2 py-1 text-sm text-gray-500">No budgets available</div>
                  ) : (
                    budgets.map((budget) => {
                      const allocated = parseFloat(budget.allocated_amount || budget.amount || 0);
                      const spent = parseFloat(budget.spent_amount || 0);
                      const remaining = parseFloat(budget.remaining_amount || (allocated - spent) || 0);
                      
                      return (
                        <SelectItem key={budget.id} value={budget.id.toString()}>
                          {budget.name} - ₦{remaining.toLocaleString()} remaining (₦{allocated.toLocaleString()} total)
                        </SelectItem>
                      );
                    })
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Link to a budget to deduct payments from budget balance ({budgets.length} budgets available)
              </p>
            </div>

            {/* Description (Optional) */}
            <div>
              <Label htmlFor="description" className="text-sm font-medium text-gray-700">
                Description (Optional)
              </Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                placeholder="Add any additional details about this payable..."
                rows={3}
                maxLength={500}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.description.length}/500 characters
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-red-600 hover:bg-red-700"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Create Payable
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreatePayableModal;
